/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the 'Software'), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED 'AS IS', WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { executeSyncRequest } from '../../../../share/util/api/syncRequest';
import apiService from '../../../../share/util/api/service';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import utils from '../../../../share/util/utils';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start', // 開始
  WAIT: 'wait', // 待機
  UPDATE_WAIT: 'updateWait', // 地図更新中表示
  UPDATE_SUCCESS: 'updateSuccess', // 地図更新成功表示
  USB_DISCONNECT: 'usbDisconnect', // USB切断確認
  SUCCESS: 'success', // 地図更新完了表示
  USB_UNCONNECTED: 'usbUnconnected', // USB未接続表示
  UPDATE_NOT_APPLICABLE: 'updateNotApplicable', // アップデート対象地図無し表示
  VEHICLE_ABNORMALITY: 'vehicleAbnormality', // 車両状態異常表示
  UPDATE_FAILED: 'updateFailed', // 地図更新失敗表示
  ERROR: 'error', // 共通エラー
};

export default {
  name: 'requirement041',
  data() {
    return {
      modal_requirement_041: false,
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      requestId: '',
      // 文言表示に使用する code のレスポンスを保持
      responseCode: '',
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },
  components: {
  },

  computed: {
    ...mapState({
      netAppId: (state) => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.USB_UNCONNECTED:
        case PHASE.UPDATE_NOT_APPLICABLE:
        case PHASE.VEHICLE_ABNORMALITY:
        case PHASE.UPDATE_FAILED:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.WAIT:
        case PHASE.UPDATE_WAIT:
        case PHASE.UPDATE_SUCCESS:
        case PHASE.USB_DISCONNECT:
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      /** 各フェーズ定義 */
      // 開始, 地図更新中表示
      const executionTitle = 'individual.common_title_execution';
      // 地図更新完了表示
      const successTitle = 'message.common_title_success';
      // 待機, 地図更新成功表示, USB切断確認
      const confirmationTitle = 'message.common_title_confirmation';
      // USB未接続表示, アップデート対象地図無し表示, 車両状態異常表示, 地図更新失敗表示, 共通エラー
      const errorTitle = 'message.common_title_error';
      const titleMap = {
        start: executionTitle,
        wait: confirmationTitle,
        updateWait: executionTitle,
        updateSuccess: confirmationTitle,
        usbDisconnect: confirmationTitle,
        success: successTitle,
        usbUnconnected: errorTitle,
        updateNotApplicable: errorTitle,
        vehicleAbnormality: errorTitle,
        updateFailed: errorTitle,
        error: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        start: '',
        wait: `${this.i18nWsKey}.REQ_WS_41_TXT_1`,
        updateWait: `${this.i18nWsKey}.REQ_WS_41_TXT_2_${this.responseCode}`,
        updateSuccess: `${this.i18nWsKey}.REQ_WS_41_TXT_3_101A`,
        usbDisconnect: `${this.i18nWsKey}.REQ_WS_41_TXT_4_1000`,
        success: `${this.i18nWsKey}.REQ_WS_41_TXT_5_1001`,
        usbUnconnected: `${this.i18nWsKey}.REQ_WS_41_TXT_6_1001`,
        updateNotApplicable: `${this.i18nWsKey}.REQ_WS_41_TXT_7_1003`,
        vehicleAbnormality: `${this.i18nWsKey}.REQ_WS_41_TXT_8`,
        updateFailed: `${this.i18nWsKey}.REQ_WS_41_TXT_9_${this.responseCode}`,
        error: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase]);
    },

    showButtonOk() {
      const showOkButtonMap = {
        start: false,
        wait: false,
        updateWait: false,
        updateSuccess: true,
        usbDisconnect: true,
        success: true,
        usbUnconnected: true,
        updateNotApplicable: true,
        vehicleAbnormality: true,
        updateFailed: true,
        error: true,
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_041 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** 実行処理 NonDefaultSession */
    async standbyProcess() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // バックエンドAPI POST,GET
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            this.phase = PHASE.WAIT;
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：汎用エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** USB接続確認（ルーチン実行,ルーチン結果確認） */
    async executeStartProcess() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // バックエンドAPI POST,GET
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute-usb',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              case 'ok':
                // 地図データVer確認
                await this.executeMapSelect();
                break;
              case 'ng':
                // USB未接続表示
                this.phase = PHASE.USB_UNCONNECTED;
                break;
              default:
                // 上記以外の結果の場合：共通エラー
                this.showUnexpectedError();
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 地図データVer確認（ルーチン実行,ルーチン結果確認） */
    async executeMapSelect() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // バックエンドAPI POST,GET
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute-map',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              case 'ok':
                // 車両状態確認
                await this.vehicleCheck();
                break;
              case 'ng':
                // アップデート対象地図無し表示
                this.phase = PHASE.UPDATE_NOT_APPLICABLE;
                break;
              default:
                // 上記以外の結果の場合：共通エラー
                this.showUnexpectedError();
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 車両状態確認（データ読出し） */
    async vehicleCheck() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // バックエンドAPI POST,GET
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              case 'ok':
                // 地図更新開始
                await this.mapUpdateStart();
                break;
              case 'ng':
              default:
                // 車両状態異常表示
                this.phase = PHASE.VEHICLE_ABNORMALITY;
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 地図更新開始（ルーチン実行,ルーチン結果確認） */
    async mapUpdateStart() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // バックエンドAPI POST,GET
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute_with_security',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              case 'updating':
                this.responseCode = response.data.user_data.progress_rate;
                // 地図更新中表示画面へ遷移
                this.phase = PHASE.UPDATE_WAIT;
                // requestIdを保持
                this.requestId = response.data.user_data.request_id;
                await this.mapUpdateWait();
                break;
              case 'success':
                // 地図更新成功表示画面へ遷移
                this.phase = PHASE.UPDATE_SUCCESS;
                break;
              case 'failure':
                this.responseCode = response.data.user_data.failure_phase;
                // 地図更新失敗表示画面へ遷移
                this.phase = PHASE.UPDATE_FAILED;
                break;
              default:
                // 上記以外：共通エラー
                this.showUnexpectedError();
                return;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 地図更新中表示画面 */
    async mapUpdateWait() {
      // ポーリングリトライ間隔で指定されている時間分待機
      await utils.sleep(process.env.VUE_APP_RETRY_INTERVAL);
      try {
        this.isLoading = true;
        // バックエンドAPI GETのみ
        const response = await executeSyncRequest(
          this,
          // GET API 呼び出しのコールバック
          async () => {
            return await apiService.getCall(
              apiService.WSP,
              `/netApps/${this.netAppId}/systems/${this.systemId}/workSupports/${this.workSupportId}/requests/${this.requestId}`
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // GET API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              case 'updating':
                this.responseCode = response.data.user_data.progress_rate;
                // 地図更新中表示画面へ遷移（再帰）
                await this.mapUpdateWait();
                break;
              case 'success':
                // 地図更新成功表示画面へ遷移
                this.phase = PHASE.UPDATE_SUCCESS;
                break;
              case 'failure':
                this.responseCode = response.data.user_data.failure_phase;
                // 地図更新失敗表示画面へ遷移
                this.phase = PHASE.UPDATE_FAILED;
                break;
              default:
                // 上記以外：共通エラー
                this.showUnexpectedError();
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** USB切断確認（ルーチン実行,ルーチン結果確認） */
    async usbDisconnect() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // バックエンドAPI POST,GET
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'monitor',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              case 'ok':
                // 地図更新完了表示
                this.phase = PHASE.SUCCESS;
                break;
              case 'ng':
                // USB切断確認再表示
                break;
              default:
                // 上記以外：共通エラー
                this.showUnexpectedError();
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * exit処理
     * @param {boolean} isUnexpectedError 共通エラーからの呼び出しフラグ
     */
    async exit(isUnexpectedError = false) {
      try {
        this.isLoading = true;
        const token = session.getToken();
        // バックエンドAPI POST,GET
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            this.closeWizard();
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 共通エラーからの呼び出しの場合、エラーの表示は不要
            if (isUnexpectedError) {
              this.closeWizard();
              return;
            }
            // 異常終了：汎用エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
      * API 通信で共通バリデーションでエラーが発生した際のコールバック
      * @param {object} result バリデーション結果
      */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.ERROR;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_041 = false;
    },

    async onOkClicked() {
      switch (this.phase) {
        case PHASE.UPDATE_SUCCESS:
          this.phase = PHASE.USB_DISCONNECT;
          break;
        case PHASE.USB_DISCONNECT:
          this.usbDisconnect();
          break;
        case PHASE.SUCCESS:
        case PHASE.USB_UNCONNECTED:
        case PHASE.UPDATE_NOT_APPLICABLE:
        case PHASE.VEHICLE_ABNORMALITY:
        case PHASE.UPDATE_FAILED:
          this.exit(false);
          break;
        case PHASE.ERROR:
          this.exit(true);
          break;
        default:
          break;
      }
    },
  },

  async mounted() {
    this.init();
    await this.standbyProcess();
  },
};